import React from 'react';

import { TextInput } from '@mantine/core';
import { IconHome } from '@tabler/icons-react';
import RegionSelect from 'components/form-fields/auto-complete-fields/RegionSelect';
import { CountryEnum } from 'types/enums';

import { useCheckout } from '../../CheckoutContext';

interface AthleteAddressInformationProps {
  /** Returning buyer with an address on file: shown in the summary, not as inputs. */
  locked?: boolean;
}

const AthleteAddressInformation = ({ locked = false }: AthleteAddressInformationProps): JSX.Element => {
  const { form, packageData } = useCheckout();
  const {
    gym: { country },
    packagesalesportalconfiguration: packageSalesPortalConfiguration,
  } = packageData;

  const { onChange: formOnAddressChange, value: formAddressValue } = form.getInputProps(
    'athleteInformation.address',
  );

  // A buyer with no address on file starts with a null form value (spec Item 6
  // root cause: reading fields off null crashed the whole step for new buyers).
  // The first keystroke builds the object, seeded with the gym's country.
  const handleAddressValueChange = (field: string, value: string | null): void => {
    formOnAddressChange({ country, ...formAddressValue, [field]: value });
  };

  return (
    <>
      {packageSalesPortalConfiguration.addressVisible && !locked && (
        <>
          <TextInput
            data-cy='addressInput'
            label='Address'
            placeholder='Enter Address'
            value={formAddressValue?.addressLines ?? ''}
            icon={<IconHome />}
            required={packageSalesPortalConfiguration.addressRequired}
            onChange={e => handleAddressValueChange('addressLines', e.currentTarget.value)}
          />
          <TextInput
            data-cy='cityInput'
            label='City'
            placeholder='Enter City'
            value={formAddressValue?.locality ?? ''}
            icon={<IconHome />}
            required={packageSalesPortalConfiguration.addressRequired}
            onChange={e => handleAddressValueChange('locality', e.currentTarget.value)}
          />
          <RegionSelect
            country={country}
            required={packageSalesPortalConfiguration.addressRequired}
            icon={<IconHome />}
            value={formAddressValue?.region ?? null}
            onChange={value => handleAddressValueChange('region', value)}
            clearable
            byCountryProps={{
              [CountryEnum.USA]: { 'data-cy': 'stateDropdown' },
            }}
          />
          {/* Free-text on purpose (spec Item 6): a US-only digit mask hard-rejects
              valid international postal codes (e.g. "SW1A 1AA", "K1A 0B1"). */}
          <TextInput
            data-cy='zipcodeInput'
            label='Zip Code'
            placeholder='Enter Zip Code'
            icon={<IconHome />}
            required={packageSalesPortalConfiguration.addressRequired}
            maxLength={16}
            value={formAddressValue?.zipCode ?? ''}
            onChange={e => handleAddressValueChange('zipCode', e.currentTarget.value)}
          />
        </>
      )}
    </>
  );
};

export default AthleteAddressInformation;
